import React, { useState } from 'react';
import { aiAPI } from '../../utils/api';
import { X, Loader2, Search, Plus, Check, AlertCircle, Globe, RefreshCw } from 'lucide-react';

var SECTORS = ['Government', 'GLC', 'Energy', 'Financial Services', 'Telecommunications', 'Healthcare', 'Education', 'Technology', 'Manufacturing', 'Real Estate'];
var ORG_TYPES = ['GLC', 'Statutory Body', 'Private Sector', 'Government Agency', 'NGO', 'Other'];

function AIDiscoverOrgsModal({ isOpen, onClose, onOrganizationsSaved }) {
  var s1 = useState(false); var loading = s1[0]; var setLoading = s1[1];
  var s2 = useState([]); var results = s2[0]; var setResults = s2[1];
  var s3 = useState({}); var selected = s3[0]; var setSelected = s3[1];
  var s4 = useState(false); var saving = s4[0]; var setSaving = s4[1];
  var s5 = useState(null); var error = s5[0]; var setError = s5[1];
  var s6 = useState(false); var saved = s6[0]; var setSaved = s6[1];
  var s7 = useState(''); var query = s7[0]; var setQuery = s7[1];
  var s8 = useState(''); var sector = s8[0]; var setSector = s8[1];
  var s9 = useState(''); var orgType = s9[0]; var setOrgType = s9[1];
  var s10 = useState([]); var allDiscovered = s10[0]; var setAllDiscovered = s10[1];
  var s11 = useState(false); var generatingMore = s11[0]; var setGeneratingMore = s11[1];

  if (!isOpen) return null;

  var handleSearch = async function(isGenerateMore) {
    if (!query.trim()) return;
    if (isGenerateMore) {
      setGeneratingMore(true);
    } else {
      setLoading(true);
      setResults([]);
      setAllDiscovered([]);
    }
    setError(null);
    setSaved(false);
    try {
      var excludeNames = isGenerateMore ? allDiscovered.slice() : [];
      var res = await aiAPI.discoverOrganizations({
        query: query,
        sector: sector,
        organization_type: orgType,
        max_results: 10,
        exclude_names: excludeNames,
      });
      var orgs = res.data.organizations || [];
      if (isGenerateMore) {
        var combined = results.concat(orgs);
        setResults(combined);
        var newNames = allDiscovered.slice();
        for (var n = 0; n < orgs.length; n++) {
          if (orgs[n].name) newNames.push(orgs[n].name);
        }
        setAllDiscovered(newNames);
        var sel = {};
        var existingKeys = Object.keys(selected);
        for (var ek = 0; ek < existingKeys.length; ek++) sel[existingKeys[ek]] = true;
        var baseIdx = results.length;
        for (var ni = 0; ni < orgs.length; ni++) {
          if (orgs[ni].confidence >= 60) sel[baseIdx + ni] = true;
        }
        setSelected(sel);
      } else {
        setResults(orgs);
        var names = [];
        for (var nn = 0; nn < orgs.length; nn++) {
          if (orgs[nn].name) names.push(orgs[nn].name);
        }
        setAllDiscovered(names);
        var initSel = {};
        for (var i = 0; i < orgs.length; i++) {
          if (orgs[i].confidence >= 60) initSel[i] = true;
        }
        setSelected(initSel);
      }
    } catch (err) {
      var msg = 'AI search failed.';
      if (err.response && err.response.data) msg = err.response.data.detail || msg;
      setError(msg);
    } finally {
      setLoading(false);
      setGeneratingMore(false);
    }
  };

  var toggleSelect = function(index) {
    var newSel = {};
    var keys = Object.keys(selected);
    for (var k = 0; k < keys.length; k++) newSel[keys[k]] = true;
    if (newSel[index]) { delete newSel[index]; } else { newSel[index] = true; }
    setSelected(newSel);
  };

  var handleSave = async function() {
    var selOrgs = [];
    for (var i = 0; i < results.length; i++) { if (selected[i]) selOrgs.push(results[i]); }
    if (selOrgs.length === 0) return;
    setSaving(true);
    try {
      await aiAPI.saveOrganizations({ organizations: selOrgs });
      setSaved(true);
      if (onOrganizationsSaved) onOrganizationsSaved();
    } catch (err) { setError('Failed to save organizations'); }
    finally { setSaving(false); }
  };

  var handleNewSearch = function() {
    setResults([]);
    setAllDiscovered([]);
    setSelected({});
    setSaved(false);
    setError(null);
  };

  var selectedCount = Object.keys(selected).length;

  var sectorOpts = [<option key="" value="">Any sector</option>];
  for (var si = 0; si < SECTORS.length; si++) sectorOpts.push(<option key={SECTORS[si]} value={SECTORS[si]}>{SECTORS[si]}</option>);
  var typeOpts = [<option key="" value="">Any type</option>];
  for (var ti = 0; ti < ORG_TYPES.length; ti++) typeOpts.push(<option key={ORG_TYPES[ti]} value={ORG_TYPES[ti]}>{ORG_TYPES[ti]}</option>);
  var resultItems = [];
  for (var ri = 0; ri < results.length; ri++) {
    resultItems.push(<OrgResultItem key={ri} idx={ri} org={results[ri]} isSel={!!selected[ri]} onToggle={toggleSelect} />);
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ai-discover-orgs-modal">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={function(e) { e.stopPropagation(); }}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div><h2 className="text-lg font-semibold text-slate-900">AI Organization Discovery</h2><p className="text-sm text-slate-500">Powered by Claude AI</p></div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}
          <div className="space-y-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Search Query *</label><input type="text" value={query} onChange={function(e) { setQuery(e.target.value); }} placeholder="e.g. Malaysian GLCs in energy sector" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ai-discover-query-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Sector</label><select value={sector} onChange={function(e) { setSector(e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ai-discover-sector">{sectorOpts}</select></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Type</label><select value={orgType} onChange={function(e) { setOrgType(e.target.value); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="ai-discover-type">{typeOpts}</select></div>
            </div>
            <button onClick={function() { handleSearch(false); }} disabled={loading || !query.trim()} className="w-full py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ai-discover-search-btn">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              {loading ? 'Searching...' : 'Discover Organizations'}
            </button>
          </div>
          {loading && <div className="text-center py-8"><Loader2 size={32} className="animate-spin text-primary mx-auto mb-3" /><p className="text-sm text-slate-600">Claude AI is researching organizations...</p></div>}
          {results.length > 0 && !loading && (
            <div className="space-y-3">
              <div className="flex items-center justify-between"><p className="text-sm font-medium text-slate-700">Found {results.length} organizations</p><p className="text-sm text-slate-500">{selectedCount} selected</p></div>
              {resultItems}
              {generatingMore && <div className="text-center py-4"><Loader2 size={24} className="animate-spin text-primary mx-auto mb-2" /><p className="text-sm text-slate-500">Finding more organizations...</p></div>}
              <div className="flex gap-3 pt-2">
                <button onClick={function() { handleSearch(true); }} disabled={generatingMore} className="flex-1 py-2 border border-primary text-primary rounded-lg text-sm hover:bg-primary/5 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ai-generate-more-btn">
                  <RefreshCw size={16} className={generatingMore ? 'animate-spin' : ''} /> Generate More
                </button>
                <button onClick={handleNewSearch} className="flex-1 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 hover:bg-slate-50" data-testid="ai-new-search-btn">New Search</button>
              </div>
              <div className="flex gap-3">
                {saved ? (
                  <div className="flex-1 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 text-center flex items-center justify-center gap-2"><Check size={16} /> Saved!</div>
                ) : (
                  <button onClick={handleSave} disabled={saving || selectedCount === 0} className="flex-1 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="ai-save-orgs-btn">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add {selectedCount} to CRM
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OrgResultItem({ idx, org, isSel, onToggle }) {
  var confColor = org.confidence >= 70 ? 'text-green-600' : org.confidence >= 40 ? 'text-amber-600' : 'text-red-500';
  var boxCls = isSel ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300';
  var checkCls = isSel ? 'bg-primary border-primary' : 'border-slate-300';
  return (
    <div onClick={function() { onToggle(idx); }} className={'p-4 rounded-lg border cursor-pointer transition-all ' + boxCls} data-testid={'ai-org-result-' + idx}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={'w-5 h-5 mt-0.5 rounded border flex items-center justify-center flex-shrink-0 ' + checkCls}>{isSel && <Check size={14} className="text-white" />}</div>
          <div>
            <div className="flex items-center gap-2"><h4 className="font-medium text-slate-900">{org.name}</h4><span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">{org.type}</span></div>
            <p className="text-sm text-slate-600 mt-0.5">{org.sector}</p>
            {org.description && <p className="text-xs text-slate-500 mt-1">{org.description}</p>}
            {org.website && <p className="text-xs text-primary mt-1 flex items-center gap-1"><Globe size={12} /> {org.website}</p>}
            {org.relevance && <p className="text-xs text-slate-400 mt-1 italic">{org.relevance}</p>}
          </div>
        </div>
        <span className={'text-xs font-medium ' + confColor}>{org.confidence}%</span>
      </div>
    </div>
  );
}

export default AIDiscoverOrgsModal;
